import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Award, Clock, CheckCircle2, ArrowLeft, Loader2, Circle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Page, PageTitle, Loading, ErrorBox } from "@/components/site/Common";
import { TradeBadge } from "@/components/site/Badges";
import { get, post, errMsg } from "@/lib/api";

export function Academy() {
  const q = useQuery({ queryKey: ["courses"], queryFn: () => get("/courses") });
  return (
    <Page>
      <PageTitle eyebrow="Formación en Oficios" title="Cursos por oficio" sub="Lecciones cortas con preguntas de evaluación. Al completarlos consigues insignias para tu perfil público." />
      <div className="mt-8">
        {q.isLoading ? <Loading /> : q.isError ? <ErrorBox /> : (
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="courses-grid">
            {q.data.map((c) => (
              <Link key={c.id} to={`/formacion/${c.id}`} data-testid={`course-card-${c.id}`} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 transition-colors hover:border-orange-300">
                <div className="flex items-center justify-between"><TradeBadge trade={c.trade} />{c.completed && <CheckCircle2 className="size-5 text-emerald-600" />}</div>
                <div className="mt-3 font-heading text-lg font-bold">{c.title}</div>
                <p className="mt-1 flex-1 text-sm text-slate-600">{c.description}</p>
                <div className="mt-4 flex items-center justify-between text-xs text-slate-500"><span className="flex items-center gap-1"><Clock className="size-3.5" />{c.duration_min} min</span><span className="flex items-center gap-1"><Award className="size-3.5 text-violet-600" />{c.badge_name}</span></div>
                <Progress value={c.progress ?? 0} className="mt-3 h-2" />
                <div className="mt-1 text-right text-xs font-semibold text-slate-600">{c.progress ?? 0}%</div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Page>
  );
}

export function Course() {
  const { id } = useParams();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["courses", id], queryFn: () => get(`/courses/${id}`) });
  const [openId, setOpenId] = useState(null);
  const [answer, setAnswer] = useState(null);
  const [busy, setBusy] = useState(false);
  if (q.isLoading) return <Page><Loading /></Page>;
  if (q.isError) return <Page><ErrorBox /></Page>;
  const c = q.data;
  const done = c.completed_lessons ?? [];
  const current = openId ?? c.lessons.find((l) => !done.includes(l.id))?.id ?? c.lessons[0].id;
  const lesson = c.lessons.find((l) => l.id === current);

  const complete = async () => {
    setBusy(true);
    try {
      const r = await post(`/courses/${id}/lessons/${lesson.id}/complete`, { answer });
      qc.setQueryData(["courses", id], r.course);
      qc.invalidateQueries({ queryKey: ["courses"], exact: true });
      if (!r.correct) return toast.error("Respuesta incorrecta. Repasa la lección e inténtalo de nuevo");
      setAnswer(null);
      setOpenId(null);
      toast.success(r.course.completed && !c.completed ? `¡Insignia conseguida: ${c.badge_name}!` : "¡Correcto! Lección completada");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <Page>
      <Link to="/formacion" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-900"><ArrowLeft className="size-4" />Formación</Link>
      <div className="mt-6 grid gap-8 lg:grid-cols-[1fr_2fr]">
        <aside className="space-y-4">
          <div className="rounded-3xl bg-slate-950 p-6 text-white">
            <TradeBadge trade={c.trade} />
            <h1 className="mt-3 font-heading text-2xl font-extrabold">{c.title}</h1>
            <Progress value={c.progress} className="mt-4 h-2 bg-white/10" />
            <div className="mt-1 text-xs text-slate-400" data-testid="course-progress">{c.progress}% · insignia «{c.badge_name}»</div>
          </div>
          <div className="space-y-2">
            {c.lessons.map((l, i) => (
              <button key={l.id} onClick={() => { setOpenId(l.id); setAnswer(null); }} data-testid={`lesson-${l.id}`}
                className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left text-sm ${l.id === current ? "border-orange-400 bg-orange-50" : "border-slate-200 bg-white"}`}>
                {done.includes(l.id) ? <CheckCircle2 className="size-5 text-emerald-600" /> : <Circle className="size-5 text-slate-300" />}
                <span className="font-semibold">{i + 1}. {l.title}</span>
              </button>
            ))}
          </div>
        </aside>
        <div className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8" data-testid="lesson-view">
          <h2 className="font-heading text-2xl font-bold">{lesson.title}</h2>
          <p className="mt-4 leading-relaxed text-slate-700">{lesson.content}</p>
          <div className="mt-8 rounded-2xl bg-slate-50 p-5">
            <div className="font-semibold">{lesson.question}</div>
            <div className="mt-3 space-y-2">
              {lesson.options.map((o, i) => (
                <button key={o} onClick={() => setAnswer(i)} data-testid={`answer-${i}`}
                  className={`w-full rounded-xl border-2 p-3 text-left text-sm font-medium ${answer === i ? "border-orange-500 bg-white" : "border-slate-200 bg-white hover:border-slate-300"}`}>{o}</button>
              ))}
            </div>
            <Button className="mt-4 rounded-xl" disabled={answer == null || busy || done.includes(lesson.id)} onClick={complete} data-testid="lesson-submit-btn">
              {busy && <Loader2 className="animate-spin" />}{done.includes(lesson.id) ? "Lección completada" : "Comprobar respuesta"}
            </Button>
          </div>
        </div>
      </div>
    </Page>
  );
}
